import type { PortfolioItemResponseDTO, StrategyItemResponseDTO } from '@/api-v2/web/discover';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import { UserModule } from '@/store/modules/user';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { isPortfolioTreeStrategy, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { DisplayName } from '@/types/settings/DisplayNamePreference';
import type { IStrategy } from '@/types/strategy';
import type { StrategyItemSubset } from '@/types/StrategyItemSubset';
import type { StrategyDisplayNameSource } from '@/types/StrategyDisplayNameSource';
import { getStrategyDisplayNameBasedOnPreference } from './strategy';

/**
 * Get the strategy display code.
 *
 * WAA-885: For private tracks or proxies, show primary identifier as:
 * "Internal Reference" > "Ticker" > "ISIN" > "Morningstar ID"
 */
export function getStrategyDisplayCode(
  strategy: IStrategy | StrategyItemResponseDTO | StrategyItemSubset | StrategyDisplayNameSource,
  shouldFallbackToName?: boolean,
): string {
  if ('trackType' in strategy) {
    if (strategy.trackType === TrackTypeConstants.PRIVATE_TRACK || strategy.trackType === TrackTypeConstants.PROXY) {
      if (strategy.internalReference) {
        return strategy.internalReference;
      }

      if ('ticker' in strategy && strategy.ticker?.[0]) {
        return strategy.ticker[0];
      }

      if ('isin' in strategy && strategy.isin?.[0]) {
        return strategy.isin[0];
      }

      if ('morningStarId' in strategy && strategy.morningStarId?.[0]) {
        return strategy.morningStarId[0];
      }

      if (shouldFallbackToName) {
        return strategy.shortName;
      }

      return '';
    }

    // For stocks, show ticker first if defined
    if (strategy.trackType === TrackTypeConstants.DATAFEED) {
      if (strategy.type === TimeSeriesEntityTypeConstants.STOCK) {
        if ('ticker' in strategy && strategy.ticker?.[0]) {
          return strategy.ticker[0].replace(/ Equity$/, '') || '-';
        }
      }
    }
  }

  // Equity screener stock rows: show Bloomberg ticker in UI (sidebar cart, CODE preference);
  if ('bbgTicker' in strategy && strategy.bbgTicker) {
    return strategy.bbgTicker;
  }

  return strategy.code;
}

export function getName(
  datum: IItemUnderAnalysis | PortfolioItemResponseDTO | StrategyItemResponseDTO | StrategyItemSubset | null,
): string {
  if (!datum) return '';

  const displayPreference = UserModule.user?.displayNamePreference ?? DisplayName.SHORT_NAME;

  if (isPortfolioTreeSubportfolio(datum)) {
    return datum.name?.trim();
  }

  if (isPortfolioTreeStrategy(datum)) {
    if (datum.strategy) {
      return getStrategyDisplayNameBasedOnPreference({ strategy: datum.strategy, preference: displayPreference });
    }

    return datum.reference?.trim() || '';
  }

  if (isStrategyFactsheetResponseDTO(datum)) {
    return getStrategyDisplayNameBasedOnPreference({ strategy: datum.strategy, preference: displayPreference });
  }

  if ('name' in datum) {
    return datum.name?.trim();
  }

  if ('code' in datum) {
    return getStrategyDisplayNameBasedOnPreference({ strategy: datum, preference: displayPreference });
  }

  // Cannot enable the code below to force TypeScript to check that we've exhausted all conditions because our
  // TypeScript version is too old
  // datum satisfies never

  return '';
}
